import { AfhandelingscenarioType as AfhandelingscenarioDto, IncidentType as IncidentDto, OngewensteGebeurtenisDto } from "@/shared/types";
import { compareDesc, parseISO } from "date-fns";

type Traject = {
    vanStation: string;
    naarStation: string;
};

export type OngewensteGebeurtenis = {
    // Velden die we niet tonen maar wel gebruiken voor de logica
    isRealtimeUpdate: boolean;
    status: "Nieuw" | "Bevestigd" | "Geweigerd";
    // Velden die in het dossier overzicht worden getoond
    relevant: boolean;
    relevantieDatum: Date | null;
    nummer: string;
    volgnummer: number;
    baanvak: string;
    trajecten: Traject[];
    omschrijving: string;
    // Velden die getoond / gebruikt worden bij het aanmaken van een nieuw sein dossier
    displayNaam: string;
    meldingStatus: string;
    treindienstLeiderGebied: string[];
    treinIncidentScenario: TreinIncidentScenario | null;
    startDatumTijd: Date;
    planaanpassing?: string;
    prognose: Prognose | null;
    betrokkenTreinen: BetrokkenTrein[];
    meldingTekst: string;
};

export const OngewensteGebeurtenis = {
    sort: (a: OngewensteGebeurtenis, b: OngewensteGebeurtenis) => {
        if (a.relevant && b.relevant && a.relevantieDatum && b.relevantieDatum) {
            return compareDesc(a.relevantieDatum, b.relevantieDatum);
        }
        return compareDesc(a.startDatumTijd, b.startDatumTijd);
    },
    fromDto: (dto: OngewensteGebeurtenisDto, isRealtimeUpdate = false): OngewensteGebeurtenis => ({
        isRealtimeUpdate: isRealtimeUpdate,
        relevant: dto.relevant,
        relevantieDatum: dto.relevantieDateTime ? new Date(dto.relevantieDateTime) : null,
        status: dto.status,
        nummer: dto.ongewensteGebeurtenis.incident.melding.meldingId,
        volgnummer: dto.ongewensteGebeurtenis.volgnummer,
        baanvak: Baanvak.fromIncidentDto(dto.ongewensteGebeurtenis.incident),
        trajecten: Traject.fromOngewensteGebeurtenisDto(dto),
        omschrijving: dto.ongewensteGebeurtenis.incident.gebeurtenissoortVerkort ?? "Onbekend",
        treindienstLeiderGebied: TreinDienstLeiderGebied.fromIncidentDto(dto.ongewensteGebeurtenis.incident),
        treinIncidentScenario: TreinIncidentScenario.fromIncidentDto(dto.ongewensteGebeurtenis.incident),
        meldingStatus: dto.ongewensteGebeurtenis.incident.melding.meldingstatus ?? "",
        displayNaam: dto.ongewensteGebeurtenis.incident.melding.meldingtekst ?? "",
        startDatumTijd: new Date(dto.ongewensteGebeurtenis.incident.melding.meldingBegintijdstip),
        planaanpassing: Planaanpassing.fromIncidentDto(dto.ongewensteGebeurtenis.incident),
        prognose: Prognose.fromIncidentDto(dto.ongewensteGebeurtenis.incident),
        betrokkenTreinen: BetrokkenTreinen.fromIncidentDto(dto.ongewensteGebeurtenis.incident),
        meldingTekst: dto.ongewensteGebeurtenis.incident.melding.meldingtekst ?? "",
    }),
};

const Baanvak = {
    fromIncidentDto: (dto: IncidentDto): string => dto.locatie?.baanvak?.code ?? "Onbekend",
};

const Traject = {
    fromOngewensteGebeurtenisDto: (dto: OngewensteGebeurtenisDto): Traject[] => dto.trajecten,
};

type Prognose = {
    datumTijd: Date;
    status: string;
    gewijzigdDatumTijd?: Date;
};

const Prognose = {
    fromIncidentDto: (dto: IncidentDto): Prognose | null => {
        const prognoses = dto.prognoses?.prognose?.filter((p) => p.prognosetijdstip);

        const sorted = prognoses?.sort(
            (a, b) => new Date(b.status?.statustijdstip ?? 0).getTime() - new Date(a.status?.statustijdstip ?? 0).getTime()
        );

        if (!sorted?.length) {
            return null;
        }

        const mostRecentPrognose = sorted[0];

        return {
            datumTijd: new Date(mostRecentPrognose.prognosetijdstip!),
            status: mostRecentPrognose?.status?.status ?? "",
            gewijzigdDatumTijd: mostRecentPrognose?.status?.statustijdstip ? new Date(mostRecentPrognose.status.statustijdstip) : undefined,
        };
    },
};

export type BetrokkenTrein = {
    code: string;
    treinNummer: number;
    handelingsVrij: boolean;
};

export const BetrokkenTreinen = {
    fromIncidentDto: (dto: IncidentDto): BetrokkenTrein[] => {
        const NSRTreinen = dto.betrokkenMaterieel?.materieel?.filter((item) => item.vervoerder?.organisatienaamAfkorting === "NSR");

        return (
            NSRTreinen?.map((NSRTrein) => ({
                code: NSRTrein?.rijkarakteristiek?.code as string,
                treinNummer: NSRTrein?.trein?.bewegingnummer as number,
                handelingsVrij: NSRTrein.reizigers?.handelingsvrijIndicator as boolean,
            })) ?? []
        );
    },
};

const TreinDienstLeiderGebied = {
    fromIncidentDto: (dto: IncidentDto): string[] =>
        dto.gebied?.trlwerkplekken?.trdlwerkplek?.filter((werkplek) => !!werkplek.naam)?.map((werkplek) => werkplek.naam as string) ?? [],
};

type TreinIncidentScenario = {
    code: string;
    gewijzigdDatumTijd: Date | undefined;
};

const TreinIncidentScenario = {
    fromAfhandelingsScenario: (dto: AfhandelingscenarioDto): TreinIncidentScenario => ({
        code: dto.tis ?? "",
        gewijzigdDatumTijd: dto.wijzigingstijdstip ? parseISO(dto.wijzigingstijdstip) : undefined,
    }),
    fromIncidentDto(dto: IncidentDto) {
        const scenarios = dto.scenarios?.scenario || [];

        const sortedScenarios = scenarios
            .filter((s) => s.tis && s.wijzigingstijdstip)
            .sort((a, b) => new Date(b.wijzigingstijdstip ?? 0).getTime() - new Date(a.wijzigingstijdstip ?? 0).getTime());

        const mostRecent = sortedScenarios[0];
        return mostRecent ? this.fromAfhandelingsScenario(mostRecent) : null;
    },
};

const Planaanpassing = {
    fromIncidentDto: (dto: IncidentDto): string | undefined => {
        const logistiekeAfhandeling = dto.logistiekeAfhandelingen?.logistiekeAfhandeling?.find(
            (item) => item.afhandelingStatus === "Geldend"
        );

        return logistiekeAfhandeling?.planAanpassingstype;
    },
};
